import { z } from "zod";

/**
 * THE DEMO DOMAIN, and it exists to be deleted.
 *
 * It is here so the boiler proves every layer end to end on day one — guard, validation,
 * role-based view, query, converter, store, screen, an upload and a push. A boiler whose
 * patterns are only described is a boiler whose patterns are wrong.
 *
 * `/setup` offers to remove it once a real domain exists.
 */
export const noteSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  /** Storage key, not a URL: signed read URLs expire and must never be persisted. */
  imageKey: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const createNoteSchema = z.object({
  title: z.string().min(1, "required").max(120),
  body: z.string().max(4000).default(""),
  imageKey: z.string().nullable().optional(),
});

export const updateNoteSchema = createNoteSchema.partial().extend({ id: z.string() });

/**
 * `coerce`, because this schema validates BOTH a JSON body and a query string, and a query
 * string has no numbers in it — `?limit=2` arrives as "2". Without it the same contract
 * accepts a payload and rejects the identical request as a query, which is a confusing way
 * to spend an afternoon.
 */
export const listNotesSchema = z.object({
  cursor: z.string().nullable().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export const noteListSchema = z.object({
  items: z.array(noteSchema),
  nextCursor: z.string().nullable(),
});

/**
 * The id in `/v1/notes/:id`. Shared rather than declared in the route, so the app rejects a
 * malformed id before the round trip and both sides mean the same thing by "an id".
 */
export const noteIdSchema = z.object({ id: z.uuid() });

/**
 * What someone opening a shared link sees: the note without its body. A second, genuinely
 * different shape rather than a description of one — the view that renders it is what proves
 * the contract is the field whitelist.
 */
export const notePublicSummarySchema = noteSchema.pick({ id: true, title: true, createdAt: true });

export type NoteDTO = z.infer<typeof noteSchema>;
export type NoteIdParam = z.infer<typeof noteIdSchema>;
export type CreateNotePayload = z.infer<typeof createNoteSchema>;
export type UpdateNotePayload = z.infer<typeof updateNoteSchema>;
export type NoteListDTO = z.infer<typeof noteListSchema>;
export type NotePublicSummaryDTO = z.infer<typeof notePublicSummarySchema>;
